/**
 * Today — the recruiter's daily worklist and the CRM landing page.
 *
 * Renders GET /api/crm/worklist as prioritized buckets, top to bottom in the
 * order they should be worked:
 *   My tasks      (reused MyTasksPanel — overdue / due-today reminders)
 *   Callbacks     booked call times overdue or in the next 24h, with one-tap
 *                 outcomes (the patient is expecting these — never miss them)
 *   Needs reply   inbound messages waiting on us, oldest first
 *   New leads     never-contacted leads (speed-to-lead: contactability decays
 *                 by the hour, so these carry an age timer)
 *   Follow-ups    cadence-due attempts ("attempt 3 of 6 · due 2h ago")
 *   Stale         mid-pipeline leads (qualified..screened) with no recent activity
 *
 * Scope is "mine + unassigned" server-side: unassigned leads appear on every
 * recruiter's list on purpose — coverage beats tidy ownership. Rows click
 * through to the contact; the list refetches on focus-relevant events
 * (crm:stats-changed) and a 60s poll, same rhythm as the sidebar badges.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarClock,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Hourglass,
  Loader2,
  MessageCircleWarning,
  PhoneMissed,
  Sparkles,
  Sun,
  Undo2,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import api from '../../services/api';
import { ContactListItem, WorklistCallback, WorklistResponse, contactName } from '../types';
import { STATS_CHANGED_EVENT, lifecycleMeta, needsReply, notifyStatsChanged, relTime, useTeam } from '../lib';
import { FollowUpsDuePanel, MyTasksPanel, PageHeader, RefreshButton, SnoozeButton } from '../components';
import { StudyChips } from '../components/shared';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

/** "Mon, Jun 15 at 3:00 PM" / "3:00 PM" in the viewer's locale. */
function slotLabel(iso: string): string {
  const d = new Date(iso);
  const sameDay = d.toDateString() === new Date().toDateString();
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return sameDay ? time : `${d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })} ${time}`;
}

const chipBase = 'inline-flex flex-shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium';
const CHIP = {
  red: `${chipBase} bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300`,
  amber: `${chipBase} bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300`,
  primary: `${chipBase} bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300`,
  gray: `${chipBase} bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300`,
};

function Section({
  icon: Icon,
  title,
  count,
  hint,
  children,
}: {
  icon: LucideIcon;
  title: string;
  count: number;
  hint: string;
  children: React.ReactNode;
}) {
  if (count === 0) return null;
  return (
    <section className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3 dark:border-gray-700">
        <Icon className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" />
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
        <span className="text-sm text-gray-500 dark:text-gray-400">({count})</span>
        <span className="ml-auto hidden text-xs text-gray-400 sm:block">{hint}</span>
      </div>
      <div className="divide-y divide-gray-100 px-4 dark:divide-gray-700">{children}</div>
    </section>
  );
}

export default function Today() {
  const navigate = useNavigate();
  const { byId } = useTeam();
  const [data, setData] = useState<WorklistResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  // Snooze (#6): leads cleared from today THIS SESSION (for the footer + Undo)
  // and whether the "Cleared for today" footer is expanded. The quick-pick menu
  // itself lives in the shared <SnoozeButton>.
  const [clearedToday, setClearedToday] = useState<ContactListItem[]>([]);
  const [clearedOpen, setClearedOpen] = useState(false);
  // Follow-ups-due count reported by <FollowUpsDuePanel>. The worklist's
  // follow_ups bucket only sees mine+unassigned overdue, so it can read 0 while
  // the panel still has later-today / teammates' follow-ups — this keeps the
  // "all caught up" card from contradicting a populated panel.
  const [followUpsDue, setFollowUpsDue] = useState(0);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await api.get('/api/crm/worklist');
      setData(res.data as WorklistResponse);
      setError(null);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const silent = () => load(true);
    const timer = window.setInterval(silent, 60_000);
    window.addEventListener(STATS_CHANGED_EVENT, silent);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener(STATS_CHANGED_EVENT, silent);
    };
  }, [load]);

  /** One-tap callback outcome: optimistic removal, revert by refetch on error. */
  const resolveCallback = async (cb: WorklistCallback, status: 'completed' | 'no_show') => {
    if (busyIds.has(cb.id)) return;
    setBusyIds((prev) => new Set(prev).add(cb.id));
    setData((prev) => prev && ({
      ...prev,
      buckets: {
        ...prev.buckets,
        callbacks: {
          count: Math.max(0, prev.buckets.callbacks.count - 1),
          items: (prev.buckets.callbacks.items || []).filter((c) => c.id !== cb.id),
        },
      },
    }));
    try {
      await api.patch(`/api/crm/callbacks/${cb.id}`, { status });
      notifyStatsChanged();
    } catch {
      load(true);
    } finally {
      setBusyIds((prev) => {
        const n = new Set(prev);
        n.delete(cb.id);
        return n;
      });
    }
  };

  /**
   * Snooze a lead off (or back onto) today's worklist. `untilIso` hides it
   * from new_leads/follow_ups/stale until that moment; `null` un-snoozes (the
   * Undo path). Optimistic: removes the row from those three buckets and tracks
   * it in the session "Cleared for today" list; reverts via refetch on error.
   * needs_reply is deliberately left alone server-side, so it never appears
   * here to be snoozed.
   */
  const snooze = async (c: ContactListItem, untilIso: string | null) => {
    if (busyIds.has(c.id)) return;
    setBusyIds((prev) => new Set(prev).add(c.id));
    if (untilIso) {
      // Optimistically pull it from the three deferrable buckets + remember it.
      setData((prev) => {
        if (!prev) return prev;
        const strip = (bucket: { count: number; items?: ContactListItem[] }) => {
          const present = (bucket.items || []).some((x) => x.id === c.id);
          return present
            ? { count: Math.max(0, bucket.count - 1), items: (bucket.items || []).filter((x) => x.id !== c.id) }
            : bucket;
        };
        return {
          ...prev,
          buckets: {
            ...prev.buckets,
            new_leads: strip(prev.buckets.new_leads),
            follow_ups: strip(prev.buckets.follow_ups),
            stale: strip(prev.buckets.stale),
          },
        };
      });
      setClearedToday((prev) => [{ ...c, snoozed_until: untilIso }, ...prev.filter((x) => x.id !== c.id)]);
    } else {
      // Undo: drop it from the session list; the refetch below restores it to
      // its real bucket.
      setClearedToday((prev) => prev.filter((x) => x.id !== c.id));
    }
    try {
      await api.patch(`/api/crm/contacts/${c.id}`, { snoozed_until: untilIso });
      notifyStatsChanged();
      if (!untilIso) load(true); // un-snooze: pull the row back into its bucket
    } catch {
      if (untilIso) setClearedToday((prev) => prev.filter((x) => x.id !== c.id));
      load(true);
    } finally {
      setBusyIds((prev) => {
        const n = new Set(prev);
        n.delete(c.id);
        return n;
      });
    }
  };

  /** Shared contact row: identity left, bucket-specific chip right. */
  const contactRow = (
    c: ContactListItem,
    context: React.ReactNode,
    chip: React.ReactNode,
    canSnooze = false,
  ) => {
    const meta = lifecycleMeta(c.lifecycle_status);
    const owner = c.owner_user_id ? byId.get(c.owner_user_id) : null;
    return (
      <div
        key={c.id}
        role="button"
        tabIndex={0}
        onClick={() => navigate(`/crm/contacts/${c.id}`)}
        onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) navigate(`/crm/contacts/${c.id}`); }}
        className="flex cursor-pointer items-center gap-3 py-3 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:bg-primary-50 dark:hover:bg-gray-700/50 dark:focus-visible:bg-primary-900/20"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className={`truncate text-sm text-gray-900 dark:text-gray-100 ${needsReply(c.last_comm) ? 'font-semibold' : 'font-medium'}`}>
              {contactName(c)}
            </span>
            <span className={`${chipBase} ${meta.badge}`}>{meta.label}</span>
            {!c.owner_user_id && <span className={CHIP.amber}>Unassigned</span>}
            {owner && <span className={CHIP.gray}>{owner.name}</span>}
          </div>
          <div className="mt-0.5 flex min-w-0 items-center gap-2">
            <span className="truncate text-xs text-gray-500 dark:text-gray-400">{context}</span>
          </div>
          <div className="mt-1">
            <StudyChips studies={c.studies} />
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <div className="flex flex-col items-end gap-1">{chip}</div>
          {canSnooze && (
            <SnoozeButton
              busy={busyIds.has(c.id)}
              onSnooze={(iso) => snooze(c, iso)}
              label={contactName(c)}
              iconClassName="h-3.5 w-3.5"
              buttonClassName="flex h-11 w-11 items-center justify-center rounded-full sm:h-9 sm:w-9 border border-gray-300 text-gray-400 transition-colors duration-150 hover:border-primary-600 hover:text-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-wait disabled:opacity-50 dark:border-gray-600"
            />
          )}
        </div>
      </div>
    );
  };

  const today = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
      </div>
    );
  }

  const b = data?.buckets;
  const leadsActionable = data
    ? data.total_actionable - data.tasks_due
    : 0;
  const allClear = data ? data.total_actionable === 0 : false;

  return (
    <div className="space-y-4">
      <PageHeader
        icon={Sun}
        title="Today"
        subtitle={today}
        actions={
          <>
            {data && !allClear && (
              <span className={CHIP.primary}>{leadsActionable} lead{leadsActionable === 1 ? '' : 's'} to work</span>
            )}
            <RefreshButton onClick={() => load()} loading={loading} label="Refresh worklist" />
          </>
        }
      />

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {error}
          <button onClick={() => load()} className="ml-2 font-medium underline hover:no-underline">Retry</button>
        </div>
      )}

      <MyTasksPanel showTeamToggle />

      <FollowUpsDuePanel onCount={setFollowUpsDue} />

      {allClear && followUpsDue === 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-10 text-center shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <CheckCircle2 className="mx-auto h-10 w-10 text-green-500" />
          <h2 className="mt-3 text-base font-semibold text-gray-900 dark:text-gray-100">You're all caught up</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            No callbacks, replies, or follow-ups are due. New leads will appear here the moment they arrive.
          </p>
        </div>
      )}

      {b && (
        <>
          <Section icon={CalendarClock} title="Callbacks" count={b.callbacks.count} hint="Booked call times — the patient is expecting you">
            {(b.callbacks.items || []).map((cb) => {
              const overdue = new Date(cb.scheduled_at).getTime() < Date.now();
              const busy = busyIds.has(cb.id);
              return (
                <div
                  key={cb.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/crm/contacts/${cb.contact.id}`)}
                  onKeyDown={(e) => {
                    // Target guard: Enter on the nested Complete/No-show buttons
                    // must not also navigate (keydown bubbles; stopPropagation
                    // on their onClick doesn't cover it).
                    if (e.key === 'Enter' && e.target === e.currentTarget) navigate(`/crm/contacts/${cb.contact.id}`);
                  }}
                  className="flex cursor-pointer items-center gap-3 py-3 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:bg-primary-50 dark:hover:bg-gray-700/50 dark:focus-visible:bg-primary-900/20"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {contactName(cb.contact)}
                      </span>
                      <span className={overdue ? CHIP.red : CHIP.primary}>{slotLabel(cb.scheduled_at)}</span>
                      {overdue && <span className={CHIP.red}>overdue</span>}
                    </div>
                    <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
                      {cb.booked_via === 'public_link' ? 'Patient picked this time' : 'Recruiter-booked'}
                      {cb.note ? ` · ${cb.note}` : ''}
                    </p>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); resolveCallback(cb, 'completed'); }}
                      disabled={busy}
                      title="Mark completed"
                      aria-label={`Mark callback with ${contactName(cb.contact)} completed`}
                      className="flex h-11 w-11 items-center justify-center rounded-full sm:h-9 sm:w-9 border border-gray-300 text-gray-400 transition-colors duration-150 hover:border-green-600 hover:text-green-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-wait disabled:opacity-50 dark:border-gray-600"
                    >
                      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); resolveCallback(cb, 'no_show'); }}
                      disabled={busy}
                      title="No-show"
                      aria-label={`Mark callback with ${contactName(cb.contact)} as no-show`}
                      className="flex h-11 w-11 items-center justify-center rounded-full sm:h-9 sm:w-9 border border-gray-300 text-gray-400 transition-colors duration-150 hover:border-red-600 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-wait disabled:opacity-50 dark:border-gray-600"
                    >
                      <PhoneMissed className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </Section>

          <Section icon={MessageCircleWarning} title="Needs reply" count={b.needs_reply.count} hint="They messaged you — oldest waiting first">
            {(b.needs_reply.items || []).map((c) =>
              contactRow(
                c,
                c.last_comm?.body || (c.last_comm?.channel === 'call' ? 'Missed call' : 'Inbound message'),
                <span className={CHIP.red}>waiting {relTime(c.needs_reply_at || c.last_activity_at)}</span>
              )
            )}
          </Section>

          <Section icon={Sparkles} title="New leads" count={b.new_leads.count} hint="Never contacted — reach out while they're warm">
            {(b.new_leads.items || []).map((c) =>
              contactRow(
                c,
                c.studies?.[0]?.lead_source ? `via ${c.studies[0].lead_source}` : 'No contact attempts yet',
                <span className={CHIP.primary}>new · {relTime(c.created_at)}</span>,
                true
              )
            )}
          </Section>

          <Section icon={Hourglass} title="Going stale" count={b.stale.count} hint={`No activity for ${data?.stale_days || 4}+ days mid-pipeline`}>
            {(b.stale.items || []).map((c) =>
              contactRow(
                c,
                `In ${lifecycleMeta(c.lifecycle_status).label.toLowerCase()} since ${relTime(c.stage_changed_at || c.created_at)}`,
                <span className={CHIP.gray}>idle {relTime(c.last_activity_at)}</span>,
                true
              )
            )}
          </Section>

          {clearedToday.length > 0 && (
            <section className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <button
                onClick={() => setClearedOpen((v) => !v)}
                className="flex w-full items-center gap-2 border-b border-gray-100 px-4 py-3 text-left transition-colors duration-150 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/50"
              >
                {clearedOpen
                  ? <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-400" />
                  : <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400" />}
                <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400">Cleared for today</h2>
                <span className="text-sm text-gray-400">({clearedToday.length})</span>
              </button>
              {clearedOpen && (
                <div className="divide-y divide-gray-100 px-4 dark:divide-gray-700">
                  {clearedToday.map((c) => (
                    <div key={c.id} className="flex items-center gap-3 py-3">
                      <div className="min-w-0 flex-1">
                        <span className="truncate text-sm font-medium text-gray-600 dark:text-gray-300">{contactName(c)}</span>
                        <p className="mt-0.5 truncate text-xs text-gray-400">
                          Snoozed until {c.snoozed_until ? slotLabel(c.snoozed_until) : 'later'}
                        </p>
                      </div>
                      <button
                        onClick={() => snooze(c, null)}
                        disabled={busyIds.has(c.id)}
                        className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-wait disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                      >
                        {busyIds.has(c.id) ? <Loader2 className="h-3 w-3 animate-spin" /> : <Undo2 className="h-3 w-3" />} Undo
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
}
